import convert from "convert-units";
import { round } from "lodash";
import { useTranslation } from "react-i18next";
import SvgIcon from "../../../../../parts/SvgIcon";
import { isTargetGoalVisibleOnDay } from "../../../../../utils/client-goals";

const OZ_PER_L = 33.8140227;

function formatDecimal(value, decimals = 1) {
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  const r = round(n, decimals);
  return Number.isInteger(r) ? String(r) : r.toFixed(decimals);
}

function kgToDisplay(kg, units) {
  const n = Number(kg);
  if (!Number.isFinite(n)) return kg;
  if (units === "imperial") {
    return convert(n).from("kg").to("lb");
  }
  return n;
}

function formatWeight(kg, units) {
  return formatDecimal(kgToDisplay(kg, units));
}

function formatWeightWithUnit(kg, units, t) {
  const value = formatWeight(kg, units);
  const unit =
    units === "imperial" ? t("common:units.lbAbbr") : t("common:units.kgAbbr");
  return `${value} ${unit}`;
}

function formatWater(liters, units, t) {
  if (liters == null || liters === "") return null;
  if (units === "imperial") {
    return `${round(Number(liters) * OZ_PER_L, 0)} ${t("common:units.ozAbbr")}`;
  }
  return `${formatDecimal(liters)}${t("common:units.literAbbr")}`;
}

function buildGoalTypeMeta(t, units) {
  return {
    sleep: {
      icon: "/img/new-icons/sleep.svg",
      label: t("calendar.cards.targetGoals.sleep"),
      getValue: (d) => {
        const hours = d.targetSleep / 3600000;
        const h = Number.isInteger(hours) ? hours : hours.toFixed(1);
        return `${h}${t("common:units.hourAbbr")}`;
      },
    },
    steps: {
      icon: "/img/new-icons/steps.svg",
      label: t("calendar.cards.targetGoals.steps"),
      getValue: (d) => Number(d.targetSteps).toLocaleString(),
    },
    bodyWeight: {
      icon: "/img/new-icons/bodyweight.svg",
      label: t("calendar.cards.targetGoals.bodyWeight"),
      getValue: (d) => formatWeightWithUnit(d.targetWeight, units, t),
    },
    hrv: {
      icon: "/img/new-icons/vuesax-linear-activity.svg",
      label: t("calendar.cards.targetGoals.hrv"),
    },
    nutrition: {
      icon: "/img/new-icons/nutrition-icon.svg",
      label: t("calendar.cards.targetGoals.nutrition"),
    },
    progress: {
      icon: "/img/new-icons/vuesax-linear-camera.svg",
      label: t("calendar.cards.targetGoals.progress"),
    },
    measurement: {
      icon: "/img/new-icons/vuesax-linear-ruler.svg",
      label: t("calendar.cards.targetGoals.measurement"),
    },
    meditate: {
      icon: "/img/new-icons/meditate.svg",
      label: t("calendar.cards.targetGoals.meditate"),
    },
    waterIntake: {
      icon: "/img/new-icons/drop-icon.svg",
      label: t("calendar.cards.targetGoals.waterIntake"),
      getValue: (d) => formatWater(d.targetWaterIntake, units, t),
    },
    personalRecord: {
      icon: "/img/new-icons/barbell.svg",
      getLabel: (d) => d.targetName,
      getValue: (d) =>
        `${d.targetReps} × ${formatWeightWithUnit(d.targetLoad, units, t)}`,
    },
    custom: {
      getIcon: (d) => `/img/new-icons/${d.customIcon}.svg`,
      getLabel: (d) => d.customName,
      getValue: (d) => d.customTarget,
    },
  };
}

function PlanTargetGoalsCard({ targetGoals, dayKey, units = "metric" }) {
  const { t } = useTranslation(["athlete-portal", "common"]);
  if (!targetGoals || targetGoals.length === 0) return null;

  const dayKeyInt = parseInt(String(dayKey).replace(/-/g, ""), 10);
  const visibleGoals = targetGoals.filter((g) =>
    isTargetGoalVisibleOnDay(g, dayKeyInt)
  );
  if (visibleGoals.length === 0) return null;

  const GOAL_TYPE_META = buildGoalTypeMeta(t, units);

  return (
    <div className="plan-card plan-card--goals">
      <div className="plan-card__header">
        <SvgIcon src="/img/new-icons/bullseye.svg" useCurrentColor={true} />
        {t("calendar.cards.targetGoals.title")}
      </div>
      {visibleGoals.map((g) => {
        const meta = GOAL_TYPE_META[g.type] || {};
        const label = meta.getLabel
          ? meta.getLabel(g.details)
          : meta.label || g.type;
        const icon = meta.getIcon
          ? meta.getIcon(g.details)
          : meta.icon || "/img/new-icons/check.svg";
        const value = meta.getValue ? meta.getValue(g.details) : undefined;
        return (
          <div key={g._id} className="plan-card__goal-row">
            <SvgIcon src={icon} />
            <span className="flex-1 truncate">{label}</span>
            {value && (
              <span className="text-(--white-50) shrink-0">{value}</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default PlanTargetGoalsCard;
